import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ThemeProvider
} from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer
} from "@mui/material";

import theme from "./theme";
import NavBar from "./components/NavBar";
import { isAdmin, getToken } from "./auth/token";
import BACKEND_URL from "./config";
import AdminInterviewsTable from "./components/AdminInterviewsTable";

// Helper to include JWT on requests
function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function InterviewsListAdmin() {
  const navigate = useNavigate();

  const [interviews, setInterviews] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typesLoading, setTypesLoading] = useState(true);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    interviewee_name: "",
    interviewee_email: "",
    interview_type_slug: ""
  });

  // Only allow admins in
  const admin = useMemo(() => isAdmin(), []);
  useEffect(() => {
    if (!admin) {
      // Not an admin — stay on this page but show a friendly message
      // If you prefer a hard redirect:
      // navigate("/", { replace: true });
    }
  }, [admin, navigate]);

  // Load interviews list
  async function fetchInterviews() {
    try {
      setLoading(true);
      setError("");
      const res = await fetch(`${BACKEND_URL}/api/interviews`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInterviews(data);
    } catch (err) {
      console.error("Error fetching interviews", err);
      setError("Failed to load interviews.");
    } finally {
      setLoading(false);
    }
  }

  // Load interview types for the dropdown
  async function fetchInterviewTypes() {
    try {
      setTypesLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/interview-types`, {
        headers: {
          "Content-Type": "application/json",
          ...authHeaders()
        }
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setInterviewTypes(data);
    } catch (err) {
      console.error("Error fetching interview types", err);
      // Show no options if this fails
    } finally {
      setTypesLoading(false);
    }
  }

  useEffect(() => {
    if (admin) {
      fetchInterviews();
      fetchInterviewTypes();
    }
  }, [admin]);

  async function handleStartInterview(e) {
    e.preventDefault();
    setError("");

    if (!form.interview_type_slug.trim()) {
      setError("You must select an interview type to start an interview.");
      return;
    }

    try {
      const payload = {
        interviewee_name: form.interviewee_name,
        interviewee_email: form.interviewee_email
      };

      const res = await fetch(
        `${BACKEND_URL}/api/interview-types/${encodeURIComponent(
          form.interview_type_slug
        )}/start-interview`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders()
          },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      const data = await res.json();
      alert(
        `Interview started!\nID: ${data.interview_id}\nType: ${data.interview_type_slug}\nProduct: ${data.product_name}\nFirst question: ${data.question_text}`
      );

      // Clear form (keep selected type)
      setForm(prev => ({
        ...prev,
        interviewee_name: "",
        interviewee_email: ""
      }));

      // Refresh list
      await fetchInterviews();
    } catch (err) {
      console.error("Error starting interview", err);
      setError(err.message || "Failed to start interview.");
    }
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  // Quick "Approve & Analyze" from list view
  async function handleApprove(interviewId, { forceReanalyze = false } = {}) {
    setError("");
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/interviews/${encodeURIComponent(interviewId)}/review`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeaders() },
          body: JSON.stringify({
            review_status: "approved",
            review_notes: forceReanalyze
              ? "Re-analyzed from list view"
              : "Approved from list view",
            force_reanalyze: forceReanalyze
          })
        }
      );

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(body.error || `HTTP ${res.status}`);
      }

      // Update local list so the row reflects new status
      setInterviews(prev =>
        prev.map(intv =>
          intv.interview_id === interviewId
            ? {
                ...intv,
                review_status: body.review_status,
                review_notes: body.review_notes,
                analysis_status: body.analysis_status,
                analysis_error: body.analysis_error,
                customer_story_filename: body.customer_story_filename
              }
            : intv
        )
      );
    } catch (err) {
      console.error("Error approving interview", err);
      setError(err.message || "Failed to approve interview.");
    }
  }

  const activeTypes = interviewTypes.filter(t => t.is_active);

  // --------- RENDER ----------
  return (
    <ThemeProvider theme={theme}>
      <NavBar />

      <Box sx={{ p: 2, maxWidth: 1200, mx: "auto" }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Interviews (Admin)
        </Typography>

        {!admin ? (
          <Paper sx={{ p: 2 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              Admins only. You don’t have access to this page.
            </Alert>
            <Button variant="outlined" onClick={() => navigate("/")}>
              Go to Home
            </Button>
          </Paper>
        ) : (
          <>
            {/* Start a new interview */}
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Start a new interview
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <form onSubmit={handleStartInterview}>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
                  <label>
                    Interviewee name:
                    <input
                      type="text"
                      name="interviewee_name"
                      value={form.interviewee_name}
                      onChange={handleChange}
                      placeholder="Optional"
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>
                  <label>
                    Interviewee email:
                    <input
                      type="email"
                      name="interviewee_email"
                      value={form.interviewee_email}
                      onChange={handleChange}
                      placeholder="Optional"
                      style={{ width: "100%", marginTop: 4 }}
                    />
                  </label>
                  <label style={{ gridColumn: "1 / -1" }}>
                    Interview type:*{" "}
                    {typesLoading ? (
                      <span>Loading interview types...</span>
                    ) : activeTypes.length === 0 ? (
                      <span>No active interview types. Create one in the admin page.</span>
                    ) : (
                      <select
                        name="interview_type_slug"
                        value={form.interview_type_slug}
                        onChange={handleChange}
                        required
                        style={{ width: "100%", marginTop: 4 }}
                      >
                        <option value="">-- Select an interview type --</option>
                        {activeTypes.map(t => (
                          <option key={t.slug} value={t.slug}>
                            {t.name || t.slug} ({t.product_name})
                          </option>
                        ))}
                      </select>
                    )}
                  </label>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Button type="submit" variant="contained" disabled={activeTypes.length === 0}>
                    Start interview
                  </Button>
                </Box>
              </form>
            </Paper>

            {/* Existing interviews */}
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Existing interviews
              </Typography>

              {loading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={22} />
                  <Typography>Loading…</Typography>
                </Box>
              ) : interviews.length === 0 ? (
                <Typography>No interviews yet.</Typography>
              ) : (
                <AdminInterviewsTable interviews={interviews} onApprove={handleApprove} />
              )}
            </Paper>
          </>
        )}
      </Box>
    </ThemeProvider>
  );
}

export default InterviewsListAdmin;
